import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Pagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import SearchBar from "./SearchBar";
import Categories from "./Categories";
import PopularTags from "./PopularTags";
import LatestBlogs from "./LatestBlogs";
import axios from "axios";
import { blogData } from "./SingleBlog/blogdata";


// Slugify helper
const slugify = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, "");

// Convert JSON-based blogData into existing static format
const mapStaticBlogs = blogData.map((post) => {
  const dateObj = new Date(post.date);
  const month = dateObj.toLocaleString("en-US", { month: "short" });
  const day = dateObj.getDate();

  return {
    id: post.id.toString(),
    subTitle: post.title,
    description: post.content?.intro?.[0] || "",
    date: { month, day },
    image: post.bannerImage,
    source: "static",
    slug: slugify(post.title),
  };
});

// Styled hover title
const BlogItem1 = styled("div")({
  cursor: "pointer",
  "&:hover": {
    color: "#30779d",
  },
});

const BlogItems: React.FC = () => {
  const [blogs, setBlogs] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const blogsPerPage = 4;
  const navigate = useNavigate();
  const clientId = "6819a43f5485188c126d1d35";

  // Fetch dynamic blogs and combine with static JSON blogs
  useEffect(() => {
    axios
      .get(`https://blog.dprprop.com/clients/${clientId}/blogs`)
      .then((res) => {
        const apiBlogs = (res.data.blogs || []).map((post: any) => {
          const createdDate = new Date(post.createdAt);
          const month = createdDate.toLocaleString("en-US", { month: "short" });
          const day = createdDate.getDate();

          return {
            id: post._id,
            subTitle: post.title,
            description: post.description || "",
            date: { month, day },
            image: `https://dprstorage.b-cdn.net${post.imageurl}`,
            slug: slugify(post.title),
            source: "dynamic",
          };
        });

        setBlogs([...mapStaticBlogs, ...apiBlogs]);
      })
      .catch((err) => {
        console.error("Error fetching blogs:", err);
        setBlogs(mapStaticBlogs); // fallback to static only
      });
  }, []);

  // Pagination logic
  const totalPages = Math.ceil(blogs.length / blogsPerPage);
  const currentBlogs = blogs.slice((page - 1) * blogsPerPage, page * blogsPerPage);

  const handleNavigation = (post: any) => {
    if (post.source === "static") {
      navigate(`/blog/${post.slug}`);
    } else {
      navigate(`/blogs/${post.slug}`);
    }
  };

  return (
    <Container>
      <Grid
        container
        spacing={6}
        sx={{ flexDirection: { xs: "column-reverse", md: "row" }, pb: 4 }}
      >
        {/* Left Blog List */}
        <Grid item xs={12} md={8} mt={6}>
          {currentBlogs.map((post, index) => (
            <Box key={post.slug} mt={index > 0 ? 4 : 0}>
              <Card>
                <Box
                  sx={{ position: "relative", cursor: "pointer" }}
                  onClick={() => handleNavigation(post)}
                >
                  <CardMedia
                    component="img"
                    image={post.image}
                    alt={post.subTitle}
                    sx={{
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.1) rotate(-1deg)",
                        background: "white",
                      },
                    }}
                  />

                  {/* Date Badge */}
                  {post?.date && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        bottom: -50,
                        left: "90%",
                        backgroundColor: "white",
                        padding: "5px 10px",
                        borderRadius: "5px",
                        fontSize: "12px",
                        zIndex: "1",
                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
                        "@media (max-width:600px)": {
                          left: "77%",
                          bottom: "-31px",
                        },
                      }}
                    >
                      <Box sx={{ fontWeight: "800", padding: "5px" }}>
                        <div>{post.date.month}</div>
                        <div>{post.date.day}</div>
                      </Box>
                    </Typography>
                  )}
                </Box>

                <CardContent
                  sx={{
                    background: "white",
                    textAlign: "left",
                    padding: "30px",
                  }}
                >
                  <BlogItem1 onClick={() => handleNavigation(post)}>
                    <h2>{post.subTitle}</h2>
                  </BlogItem1>

                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.775rem", lg: "0.87rem" },
                    }}
                  >
                    {post.description}
                  </Typography>

                  <Box
                    sx={{
                      mt: { xs: 5, lg: 2 },
                      borderBottom: "1px solid #74787C",
                      pb: 1,
                      width: { xs: "155px", lg: "fit-content" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: "32px", lg: "0.9rem" },
                        color: "#30779d",
                        "&:hover": { color: "#000000" },
                        display: "block",
                      }}
                    >
                      Read More
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
            />
          </Box>
        </Grid>

        {/* Right Sidebar */}
        <Grid item xs={12} md={4} mt={6}>
          <SearchBar />
          <Box mt={4}>
            <LatestBlogs />
          </Box>
          <Box mt={4} mb={4}>
            <PopularTags />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BlogItems;
